// HQ Admin — Marketing+ Templates library.
//
// Manages the pool of ready-made e-shot templates franchisees can start
// their own Marketing+ campaigns from. **Lean editor**: the create/edit
// experience delegates to the same `MarketingComposeModal` the
// franchisee uses in their portal — passed with `mode="template"` so it
// hides recipients/bookings/footer/send and swaps in the small amount
// of template-specific chrome (Name + Description + Save / Publish /
// Unpublish / Archive). One composer/editor, two contexts.
//
// Franchisee behaviour is unchanged: they hit "Start from a Mojo
// template", the backend creates an independent detached campaign
// copy, and it opens in their normal `MarketingComposeModal` (campaign
// mode). Later HQ edits never propagate back into a franchisee copy.
import { useCallback, useEffect, useState } from "react";
import {
  Megaphone, Plus, Loader2, AlertCircle, CheckCircle2,
  Edit3, Send, Archive, RefreshCw, Copy, Trash2, X,
} from "lucide-react";
import api from "@/lib/api";
import MarketingComposeModal from "@/components/portal/marketing/MarketingComposeModal";

const STATUS_LABEL = {
  draft: "Draft",
  published: "Published",
  archived: "Archived",
};
const STATUS_STYLE = {
  draft: "bg-stone-100 text-stone-700",
  published: "bg-emerald-100 text-emerald-800",
  archived: "bg-stone-200 text-stone-500",
};

export default function AdminMarketingTemplatesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [editing, setEditing] = useState(null);   // template doc | "new" | null
  const [confirmDelete, setConfirmDelete] = useState(null);  // template doc | null
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = filter === "all" ? "?status=all" : `?status=${filter}`;
      const { data } = await api.get(`/admin/marketing/templates${params}`);
      setItems(data.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load templates");
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const publish = async (id) => {
    try {
      await api.post(`/admin/marketing/templates/${id}/publish`);
      setInfo("Template published — franchisees can now use it.");
      load();
    } catch (e) { setError(e?.response?.data?.detail || "Publish failed"); }
  };
  const unpublish = async (id) => {
    try {
      await api.post(`/admin/marketing/templates/${id}/unpublish`);
      setInfo("Template unpublished.");
      load();
    } catch (e) { setError(e?.response?.data?.detail || "Unpublish failed"); }
  };
  const archive = async (id) => {
    if (!window.confirm("Archive this template? Archived templates disappear from the franchisee picker.")) return;
    try {
      await api.post(`/admin/marketing/templates/${id}/archive`);
      setInfo("Template archived.");
      load();
    } catch (e) { setError(e?.response?.data?.detail || "Archive failed"); }
  };

  const editorOpen = editing !== null;
  // The shared composer needs a minimal `access` object with sane
  // template-mode defaults so the (hidden) footer/bookings toggles
  // don't crash when re-hydrating a saved template.
  const templateAccess = {
    bookings_enabled: false,
    phone: "",
    from_email: "headoffice@creativemojo.co.uk",
    facebook_url: "",
    instagram_url: "",
    custom_link_url: "",
    logo_target: "mojo",
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="admin-marketing-templates-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 flex items-center gap-1.5">
            <Megaphone className="w-3 h-3" /> Marketing+ · Head Office
          </div>
          <h1 className="font-display text-3xl font-black text-stone-950 mt-1">Marketing+ Templates</h1>
          <p className="text-sm text-stone-600 mt-1 max-w-2xl">
            Author ready-made Marketing+ e-shots using the same visual composer franchisees use.
            Published templates appear in every franchisee's "Start from a Mojo template" picker —
            each franchisee gets their own detached editable copy. Later edits to a template never
            alter existing franchisee campaigns.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing("new")}
          data-testid="admin-marketing-template-new"
          className="px-4 py-2.5 bg-stone-950 hover:bg-stone-800 text-white rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"
        >
          <Plus className="w-3.5 h-3.5" /> New Template
        </button>
      </div>

      <div className="mb-4 flex items-center gap-2">
        {[
          { id: "all", label: "All" },
          { id: "draft", label: "Drafts" },
          { id: "published", label: "Published" },
          { id: "archived", label: "Archived" },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`admin-marketing-templates-filter-${f.id}`}
            className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-lg border ${filter === f.id
              ? "border-stone-950 bg-stone-950 text-white"
              : "border-stone-300 hover:bg-stone-50 text-stone-700"}`}
          >{f.label}</button>
        ))}
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 text-red-800 text-sm flex items-center gap-2" data-testid="admin-marketing-templates-error">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}
      {info && !error && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-800 text-sm flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> {info}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-stone-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading templates…
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-stone-200 rounded-xl text-stone-500">
          <p className="text-sm">No templates yet. Click "New Template" to author one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="admin-marketing-templates-list">
          {items.map((t) => (
            <div
              key={t.id}
              className="bg-white border border-stone-200 rounded-xl overflow-hidden flex flex-col"
              data-testid={`admin-marketing-template-${t.id}`}
            >
              <div className="aspect-[16/9] bg-stone-100 flex items-center justify-center overflow-hidden">
                {t.thumbnail_url ? (
                  <img src={t.thumbnail_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Megaphone className="w-10 h-10 text-stone-300" />
                )}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-bold text-stone-950 truncate">{t.name || "Untitled"}</h3>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${STATUS_STYLE[t.status] || STATUS_STYLE.draft}`}>
                    {STATUS_LABEL[t.status] || t.status}
                  </span>
                </div>
                {t.description && (
                  <p className="text-xs text-stone-600 mt-1 line-clamp-2">{t.description}</p>
                )}
                <div className="mt-auto pt-3 flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setEditing(t)}
                    data-testid={`admin-marketing-template-edit-${t.id}`}
                    className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-stone-300 hover:bg-stone-50 rounded-lg flex items-center gap-1"
                  ><Edit3 className="w-3 h-3" /> Edit</button>
                  {t.status === "draft" && (
                    <button
                      onClick={() => publish(t.id)}
                      data-testid={`admin-marketing-template-publish-${t.id}`}
                      className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center gap-1"
                    ><Send className="w-3 h-3" /> Publish</button>
                  )}
                  {t.status === "published" && (
                    <button
                      onClick={() => unpublish(t.id)}
                      data-testid={`admin-marketing-template-unpublish-${t.id}`}
                      className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-stone-300 hover:bg-stone-50 rounded-lg flex items-center gap-1"
                    ><RefreshCw className="w-3 h-3" /> Unpublish</button>
                  )}
                  {t.status !== "archived" && (
                    <button
                      onClick={() => archive(t.id)}
                      data-testid={`admin-marketing-template-archive-${t.id}`}
                      className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-stone-500 hover:text-red-600 rounded-lg flex items-center gap-1"
                    ><Archive className="w-3 h-3" /> Archive</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* The one composer, two contexts. Template mode uses the same
          visual editor + live preview as the franchisee composer. */}
      <MarketingComposeModal
        open={editorOpen}
        mode="template"
        template={editing === "new" ? null : editing}
        access={templateAccess}
        draft={null}
        preselectClientId={null}
        onClose={() => setEditing(null)}
        onTemplateSaved={(saved) => {
          // Refresh the list so status changes show without closing the
          // modal. Users close manually via Cancel — matches campaign UX.
          load();
          if (saved) setEditing(saved);
        }}
      />
    </div>
  );
}
